import * as React from 'react'
import { LiaisonHeadshot } from './HeadShot'
import { cn } from '../../../lib/utils'
import AccoladeCards, { LiaisonAccoladeCards } from './AccoladeCards'
import { useTranslation } from '~/hooks/useTranslation'
import { Button } from '~/lib/ui/button'
import { LinkedInIcon } from '~/components/coach_services/ProfessionalServices/icons'
import LeaveSiteWarning from '~/components/shared/LeaveSiteWarning/index'
import { useIsMobile } from '~/hooks/useMediaQuery'
import HeadshotCircle from './HeadshotCircle'

export default function CoachCard({
  coach,
  coach_photo,
  transparent_coach_photo,
  accolades,
  liaison,
  liaison_image_url,
  linkedin_url,
  ...props
}) {
  const coachImage = transparent_coach_photo || coach_photo
  const { t } = useTranslation()

  const isMobile = useIsMobile()

  if (liaison)
    return <LiaisonCoachCard coachImage={liaison_image_url} {...props} />
  return isMobile ? (
    <CoachCardMobile
      coachImage={coachImage}
      coach={coach}
      linkedin_url={linkedin_url}
      t={t}
    />
  ) : (
    <CoachCardDesktop
      coachImage={coachImage}
      coach={coach}
      accolades={accolades}
      linkedin_url={linkedin_url}
      t={t}
    />
  )
}

function ensureProtocol(url) {
  if (!url) return ''
  return url.startsWith('http://') || url.startsWith('https://')
    ? url
    : `https://${url}`
}

const CoachCardDesktop = ({
  coachImage,
  coach,
  accolades,
  linkedin_url,
  t,
}) => {
  const { yearsExperience, numClientsSupported } = accolades
  const satisfactionRating = accolades.satisfactionRating
  return (
    <div
      className={cn(
        'grid grid-cols-3 gap-y-4 rounded-[20px] bg-[#E9F2E2] px-2 pb-0 pt-2 xl:static xl:grid-cols-[340px_repeat(3,_minmax(0,_1fr))] xl:gap-x-6 xl:px-8 xl:pt-6',
      )}
    >
      <div className="relative flex items-end xl:col-span-1 xl:row-span-2 xl:mb-3 xl:ml-10 xl:mr-auto">
        <HeadshotCircle coachImage={coachImage} />
      </div>
      <div className="col-span-2 flex flex-row items-center xl:col-span-3 xl:items-center xl:pt-2">
        <div className="flex w-full flex-col xl:flex-row xl:items-center">
          <div className="block font-roboto text-[30px] font-bold uppercase text-intoo-green-coachcta xl:text-[32px]">
            {coach.first_name + ' ' + coach.last_name}
          </div>
          {linkedin_url && (
            <div className="mt-4 w-full xl:ml-6 xl:mt-0 xl:w-auto">
              <LeaveSiteWarning url={ensureProtocol(linkedin_url)}>
                <Button variant="coach_linkedin" size="md">
                  <LinkedInIcon
                    className="mr-2 h-6 w-6"
                    strokecolor="#0077B5"
                  />
                  {t('view_linkedin_profile')}
                </Button>
              </LeaveSiteWarning>
            </div>
          )}
        </div>
      </div>
      <AccoladeCards
        yearsExperience={yearsExperience}
        numClientsSupported={numClientsSupported}
        satisfactionRating={satisfactionRating}
        className={cn(
          'invisible fixed mb-8 max-h-20 xl:visible xl:static xl:flex',
        )}
      />
    </div>
  )
}

const CoachCardMobile = ({ coachImage, coach, linkedin_url, t }) => {
  return (
    <div
      className={cn(
        'flex flex-col items-center rounded-[20px] bg-[#E9F2E2] px-4 py-4',
      )}
    >
      <div className="mb-2 flex w-full flex-row items-center justify-center">
        <HeadshotCircle coachImage={coachImage} variant="mobile" />
        <div className="flex flex-1 items-center justify-center">
          <span className="text-center font-roboto text-[22px] font-bold uppercase text-intoo-green-coachcta">
            {coach.first_name + ' ' + coach.last_name}
          </span>
        </div>
      </div>
      {linkedin_url && (
        <div className="mt-2 w-full">
          <LeaveSiteWarning url={ensureProtocol(linkedin_url)}>
            <Button variant="coach_linkedin" size="md" className="w-full">
              <LinkedInIcon className="mr-2 h-6 w-6" strokecolor="#0077B5" />
              {t('view_linkedin_profile')}
            </Button>
          </LeaveSiteWarning>
        </div>
      )}
    </div>
  )
}

export function LiaisonCoachCard({ coachImage, selectProgram }) {
  const { t } = useTranslation('accolades')

  return (
    <div className={cn('flex items-center rounded-[20px] bg-[#E9F2E2]')}>
      <div className="flex justify-center lg:w-[19rem]">
        <LiaisonHeadshot
          className="h-[5.625rem] w-[5.625rem] rounded-full border-2 border-solid border-intoo-green-coachcta bg-[#FFFFFF] object-cover shadow-md md:h-[12rem] md:w-[12rem]"
          coachImage={coachImage}
        />
      </div>

      <div className="flex flex-col gap-4 p-4 lg:flex-1 lg:p-8">
        <div className="block font-roboto text-[30px] font-bold uppercase text-intoo-green-coachcta xl:text-[32px]">
          {selectProgram ? t('select_liaison_team') : t('coach_liaison_team')}
        </div>
        <div className="flex gap-12">
          <LiaisonAccoladeCards
            className={cn(
              'invisible fixed mb-8 flex-1 xl:visible xl:static xl:flex',
            )}
          />
        </div>
      </div>
    </div>
  )
}
